import React from "react";
import { graphql, HeadProps, Script, useStaticQuery } from "gatsby";
import { PageContextIntl } from "@src/components/general/WrapPageElement/PageContext";
import { GATSBY_SOURCE_SUSHISHOP_BASE_URL } from "@src/utils/constants";
// import SuspenseAfterInteraction from "../AfterInteraction/SuspenseAfterInteraction";
// import SuspenseAfterSetup from "../AfterSetup/SuspenseAfterSetup";

export type DefaultHeadProps<
  DataType = object,
  PageContextType = PageContextIntl
> = HeadProps<DataType, PageContextType> & {
  children?: React.ReactNode;
  title?: string;
  description?: string;
};

const BASE_URL = GATSBY_SOURCE_SUSHISHOP_BASE_URL;
const APPLE_ITUNES_APP = process.env.GATSBY_APPLE_ITUNES_APP || "";

const GetSeoConfigurations = (language: string) => {
  const data = useStaticQuery<Queries.AllSeoConfigurationsQuery>(graphql`
    query AllSeoConfigurations {
      _META_TITLE_: allConfiguration(filter: { name: { eq: "_META_TITLE_" } }) {
        nodes {
          value
          lang
        }
      }
      _META_DESCRIPTION_: allConfiguration(
        filter: { name: { eq: "_META_DESCRIPTION_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _GOOGLE_SITE_VERIF_: allConfiguration(
        filter: { name: { eq: "_GOOGLE_SITE_VERIF_" } }
      ) {
        nodes {
          value
          lang
        }
      }
    }
  `);
  const getLangValue = (
    {
      nodes,
    }: {
      readonly nodes: readonly {
        value?: string | null;
        lang?: string | null;
      }[];
    },
    defaultValue: string
  ) => nodes.find((node: any) => node.lang === language)?.value || defaultValue;
  const _META_TITLE_ = getLangValue(data._META_TITLE_, "Sushi Shop");
  const _META_DESCRIPTION_ = getLangValue(data._META_DESCRIPTION_, "");
  const _GOOGLE_SITE_VERIF_ = getLangValue(data._GOOGLE_SITE_VERIF_, "");
  return {
    _META_TITLE_,
    _META_DESCRIPTION_,
    _GOOGLE_SITE_VERIF_,
  };
};
const DefaultHead = (headProps: DefaultHeadProps) => {
  const { pageContext, children, title = "", description = "" } = headProps;
  const { intl, meta_title: contextMetaTitle = "" } = pageContext;
  const { language, alternates = [] } = intl;
  const { _META_TITLE_, _META_DESCRIPTION_, _GOOGLE_SITE_VERIF_ } =
    GetSeoConfigurations(language);

  let meta_title = title || contextMetaTitle || _META_TITLE_;
  let meta_description = description || _META_DESCRIPTION_;
  const FAVICON_BASE_URL = `${BASE_URL}/FRONTOFFICE/views/risingsun/img/favicons/`;
  const appleTouchIcons = [
    "57x57",
    "60x60",
    "72x72",
    "76x76",
    "114x114",
    "120x120",
    "144x144",
    "152x152",
    "180x180",
  ].map((size) => ({
    sizes: size,
    rel: "apple-touch-icon",
    href: `${FAVICON_BASE_URL}apple-touch-icon-${size}.png`,
  }));
  const favicons = ["16x16", "32x32", "96x96", "192x192", "favicon.ico"].map(
    (size) => {
      let rel = "icon";
      let icon_filename = `favicon-${size}.png`;
      let type = "image/png";
      if (size === "favicon.ico") {
        rel = "shortcut icon";
        icon_filename = "favicon.ico";
        type = "image/x-icon";
      } else if (size === "192x192") {
        icon_filename = "android-chrome-192x192.png";
      }

      const href = `${FAVICON_BASE_URL}${icon_filename}`;
      return {
        sizes: size,
        rel,
        href,
        type,
      };
    }
  );
  const icons = [...favicons, ...appleTouchIcons];

  return (
    <>
      <html lang={language} />
      <title key={"default_meta_title"}>{meta_title}</title>
      <meta key="og:title" property="og:title" content={meta_title} />
      <meta key="description" name="description" content={meta_description} />
      <meta
        key="og:description"
        name="og:description"
        content={meta_description}
      />
      <div dangerouslySetInnerHTML={{ __html: _GOOGLE_SITE_VERIF_ }}></div>

      {alternates?.map(({ lang, uri }, key) => (
        <link
          key={key}
          href={BASE_URL + uri}
          {...(lang === language
            ? { rel: "canonical" }
            : { rel: "alternate", hrefLang: lang })}
        />
      ))}
      {APPLE_ITUNES_APP && (
        <meta
          key="apple-itunes-app"
          name="apple-itunes-app"
          content={APPLE_ITUNES_APP}
        />
      )}
      {icons.map((linkProps, key) => (
        <link {...{ key }} {...linkProps} />
      ))}
      {children}
    </>
  );
};

export default DefaultHead;
